import { OnInit, OnDestroy, Directive, ElementRef, HostListener, Input } from '@angular/core';
import { NgControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import UtilForm from '@shared/utilities/util-form.utility';

@Directive({
  selector: '[appFormControl]',
})
export class AppFormControlDirective implements OnInit, OnDestroy {

  static readonly INPUT_CLASS = 'form-input';
  static readonly INVALID_INPUT_CLASS = 'is-error';
  static readonly VALID_INPUT_CLASS = 'is-success';

  private readonly destroy$ = new Subject<void>();
  private readonly ENTER_KEY_CODE = 13;
  private readonly TYPES: string[] = [
    'password',
    'select-one',
  ];

  private isCleanValue: boolean;

  @Input() readonly needNull = true;
  @Input() readonly needTrim = true;

  @HostListener('focusout') onBlur() {
    if (!this.isCleanValue) {
      this.clearValue();
    }
    this.isCleanValue = false;
  }

  @HostListener('keydown', ['$event']) onKeydown(event: KeyboardEvent) {
    if (event.keyCode === this.ENTER_KEY_CODE) {
      this.clearValue();
      this.isCleanValue = true;
    }
  }

  constructor(
    private inputField: ElementRef,
    private formControl: NgControl
  ) { }

  ngOnInit(): void {
    this.formControl.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.setInvalidClass(),
      });

    this.inputField.nativeElement.classList['add'](AppFormControlDirective.INPUT_CLASS);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private isValid(): boolean {
    return !UtilForm.isInvalidControl(this.formControl);
  }

  private setInvalidClass(): void {
    const inValidState = this.isValid() ? 'remove' : 'add';
    const validState = this.isValid() && this.formControl.value ? 'add' : 'remove';

    if (this.formControl.touched) {
      this.inputField.nativeElement.classList[validState](AppFormControlDirective.VALID_INPUT_CLASS);
      this.inputField.nativeElement.classList[inValidState](AppFormControlDirective.INVALID_INPUT_CLASS);
    }
  }

  private setEmptyToNull(): void {
    if (this.needNull && this.formControl.value === '') {
      this.formControl.control.setValue(null);
    }
  }

  private trimValue(): void {
    if (this.needTrim && this.formControl.value && typeof this.formControl.value === 'string') {
      this.formControl.control.setValue(this.formControl.value.trim());
    }
  }

  private clearValue(): void {
    const type: string = this.inputField.nativeElement.type;
    if (!this.TYPES.includes(type)) {
      this.trimValue();
      this.setEmptyToNull();
    }
    this.formControl.control.updateValueAndValidity();
  }

}
